/* import __COLOCATED_TEMPLATE__ from './company-profile-members.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { dropTask, task } from 'ember-concurrency';
import { action } from '@ember/object';
import { showModal } from 'sh-www/utils/modal-controls';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-company-profile-members';

class CompanyMembers extends Component {
  @service('state-managers/application') applicationStateManager;
  @service media;
  @service session;

  @tracked adminsSectionShown = true;
  @tracked companyMembers = [];
  @tracked companyTeams = [];
  @tracked deactivatedSectionShown = false;
  @tracked invites = [];
  @tracked membersSectionShown = true;
  @tracked pendingSectionShown = false;
  @tracked teamFilter = null;

  get adminListClassNames() {
    return clsx(
      this.showLinebreak && `${CLASS_NAME}__list--bordered-bottom`,
    );
  }

  get classNames() {
    return clsx(
      CLASS_NAME,
      this.args.class,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  #membersAdminCache = createCache(() => {
    return this.companyMembers
      .filter(employee => employee.companyMembership.admin && employee.companyMembership.editor)
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  });

  get membersAdmin() {
    return getValue(this.#membersAdminCache);
  }

  #membersNonAdminCache = createCache(() => {
    return this.companyMembers
      .filter(employee => !employee.companyMembership.admin)
      .sort((a, b) => a.lastName.localeCompare(b.lastName));
  });

  get membersNonAdmin() {
    return getValue(this.#membersNonAdminCache);
  }

  get membersNonAdminFiltered() {
    const { teamIdToTeamMap, teamFilter } = this;

    if (teamFilter) {
      return teamIdToTeamMap[teamFilter].companyTeamMemberships.reduce((accum, { companyMembership }) => {
        if (!companyMembership.isDeactivated && companyMembership.editor) {
          accum.push(companyMembership.user);
        }

        return accum;
      }, []);
    }

    return this.membersNonAdmin.filter(({ companyMembership }) => !companyMembership.isDeactivated && companyMembership.editor);
  }

  get membersDeactivated() {
    return this.membersNonAdmin.filter(({ companyMembership }) => companyMembership.isDeactivated);
  }

  get currentUserIsCompanyAdminOrAdmin() {
    const { currentUser } = this.session;
    return currentUser.isCompanyAdmin || currentUser.isAdmin;
  }

  get teamFilterValue() {
    return this.companyTeams.reduce((accum, team) => {
      accum[team.id] = team.name;
      return accum;
    }, {
      [null]: 'All Teams',
    });
  }

  get teamIdToTeamMap() {
    return this.companyTeams.reduce((accum, team) => {
      accum[team.id] = team;
      return accum;
    }, {});
  }

  get userIdToCompanyTeamsMap() {
    return this.companyMembers.reduce((accum, user) => {
      accum[user.id] = user.companyMembership.companyTeamMemberships.map(({ companyTeam }) => companyTeam);
      return accum;
    }, {});
  }

  get showMembersTeamFilter() {
    return this.membersSectionShown && this.companyTeams.length > 0;
  }

  get options() {
    return [
      {
        key: 'All Teams',
        model: null,
      },
      ...this.companyTeams.map((team) => ({
        key: team.name,
        model: team.id,
      })),
    ];
  }

  get showLinebreak() {
    return Boolean(this.membersAdmin.length && this.membersNonAdmin.length);
  }

  @task
  *initializeData(companyId, includeTeams = false) {
    const company = yield this.args.loadCompanyMembersTask.linked().perform(companyId, includeTeams);

    this.companyMembers = this.currentUserIsCompanyAdminOrAdmin
      ? company.allEmployees
      : company.approvedEmployees;

    if (this.currentUserIsCompanyAdminOrAdmin) {
      this.invites = company.invites.filter(({ isAccepted }) => {
        return !isAccepted;
      });
    }

    if (includeTeams) {
      this.companyTeams = company.companyTeams;
    }
  }

  @dropTask
  *reloadCompanyMembers(companyId) {
    const company = yield this.args.loadCompanyMembersTask.linked().perform(companyId, true);
    this.companyMembers = this.currentUserIsCompanyAdminOrAdmin ? company.allEmployees : company.approvedEmployees;
  }

  @task
  *loadCompanyTeams(companyId) {
    return yield this.args.reloadCompanyTeamsTask.perform(companyId);
  }

  /**
   * Show the invite to company modal
   */
  @action
  onInviteCompanyMemberClicked() {
    const { company } = this.args;
    if (this.session.currentUser.isAdmin) {
      const modalModel = {
        company,
        onInviteSucceeded: (invite) => {
          this.applicationStateManager.addToast('Invite sent');
          this.invites = [...this.invites, invite];
        },
      };

      showModal(this, 'admin/invite-to-company-admin-modal', modalModel);
      return;
    }

    const modalModel = {
      company,
      handleSendInviteSuccess: (invite) => {
        this.applicationStateManager.addToast('Invite sent');
        const previousInvites = this.invites.filter(({ email }) => email !== invite.email);

        this.invites = [...previousInvites, invite];
      }
    };

    showModal(this, 'company/invite-to-company-modal', modalModel);
  }

  @action
  toggleSection(name) {
    this[`${name}SectionShown`] = !this[`${name}SectionShown`];
  }

  @action
  onTeamFilterSelected(team) {
    this.teamFilter = team;
  }

  @action
  removeInvite(inviteId) {
    const { invites } = this;
    this.invites = invites.filter(({ id }) => id !== inviteId);
  }

  @action
  updateCompanyTeams(updatedCompanyTeams) {
    this.companyTeams = updatedCompanyTeams;
  }

  constructor() {
    super(...arguments);
    this.initializeData.perform(this.args.company.id, this.args.company.canUseCompanyTeams);
  }
}

export default CompanyMembers;
